import { useTranslation } from "react-i18next";
import { useEffect, useState, forwardRef, useMemo } from "react";
import IconComponent from "../../icons";
import { useResizeDetector } from "react-resize-detector";
import Button from "@mui/material/Button";
import ButtonComponent from "../../form/ButtonComponent";
import { BUTTON_TYPES } from "../../constants/globalVariable";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ToolTipComponent from "../tooltip";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import IconButton from "@mui/material/IconButton";
import PropTypes from "prop-types";

const ToolBar = forwardRef(
  (
    {
      toolbar = [],
      toolbarClickHandler,
      showToolbarBorder = false,
      enableColumnChooser = false,
      columnDefinitions = [],
      propertiesPanel,
      callbackForVisibleColumnsUpdate,
      height = 12,
      excelExportExtension,
      excelExport,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [showOverFlowBtns, setShowOverFlowBtns] = useState(false);
    const { width, ref: toolRef } = useResizeDetector();
    const gridRef = ref;
    const [anchorEl, setAnchorEl] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [showPropertiesPanel, setShowPropertiesPanel] = useState(false);

    useEffect(() => {
      if (toolRef && toolRef.current) {
        const ele = toolRef.current;
        if (ele.scrollWidth - ele.scrollLeft !== ele.clientWidth) {
          setShowOverFlowBtns(true);
        } else {
          setShowOverFlowBtns(false);
        }
      }
    }, [width]);

    const rightArray = useMemo(() => toolbar?.filter((item) => item.rightAllign && !item.sticky), [toolbar]);
    const leftArray = useMemo(() => toolbar?.filter((item) => !item.rightAllign && !item.sticky), [toolbar]);
    const rightStickyArray = useMemo(() => toolbar?.filter((item) => item.rightAllign && item.sticky), [toolbar]);
    const leftStickyrray = useMemo(() => toolbar?.filter((item) => !item.rightAllign && item.sticky), [toolbar]);

    const [selectedColumns, setSelectedColumns] = useState([]);

    useEffect(() => {
      if (gridRef?.current?.gridService) {
        const visibleColumns = columnDefinitions.filter((col) => !col.hidden);
        setSelectedColumns(visibleColumns);
      }
    }, [columnDefinitions, gridRef]);

    const handleColumnsChange = (column) => {
      if (!gridRef?.current) return;
      callbackForVisibleColumnsUpdate([column]);
    };

    const handleColumnChooserOpen = (e) => {
      setAnchorEl(e.currentTarget);
    };

    const handleColumnChooserClose = () => {
      setAnchorEl(null);
    };

    const handleArrowClick = (direction) => {
      if (toolRef.current) {
        const scrollAmount = direction === "forward" ? 200 : -200;
        toolRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    };

    const handleClick = (id) => {
      if (toolbarClickHandler) {
        toolbarClickHandler(id);
      }
    };

    const handleExportToExcel = () => {
      excelExportExtension.exportToExcel();
    };

    const handleToggleProperties = () => {
      setShowPropertiesPanel((prev) => !prev);
      if (toolbarClickHandler) {
        toolbarClickHandler("properties");
      }
    };

    const renderToolbarItem = (item, index) => {
      return (
        <div
          className={`flex items-center ${
            item.disabled
              ? "opacity-40 cursor-not-allowed grayscale select-none pointer-events-auto [&>*]:pointer-events-none"
              : ""
          } ${item.hidden ? " hidden" : ""}`}
          key={index}
        >
          {!item.template && (
            <ButtonComponent
              {...item}
              key={item.id}
              sx={{ flexShrink: 0 }}
              className={`${index === 0 ? "ms-3" : ""} ${" " + item.cssClass} `}
              buttonType={item.buttonType || BUTTON_TYPES.BACKGROUND_BUTTON}
              onClick={() => {
                handleClick(item.id);
              }}
            >
              <div className="flex items-center">
                <div className="me-1">
                  {item.prefixIcon && <IconComponent name={item.prefixIcon} />}
                  {item.prefixIconTemplate && item.prefixIconTemplate}
                </div>
                {item.name}
                <div className="ms-1">
                  {item.suffixIcon && <IconComponent name={item.suffixIcon} />}
                  {item.suffixIconTemplate && item.suffixIconTemplate}
                </div>
              </div>
            </ButtonComponent>
          )}
          {item.template && item.template()}
        </div>
      );
    };

    return (
      <>
        {toolbar?.length > 0 && (
          <div
            className={`overflow-hidden flex gap-5 items-center bg-white ${
              showToolbarBorder && "border-b border-[var(--button-light)]"
            } h-${height}`}
          >
            {showOverFlowBtns && (
              <div className="flex-shrink-0 flex items-center h-full cursor-pointer">
                <Button
                  sx={{
                    height: "100%",
                    minWidth: "36px",
                    borderInlineEnd: "1px solid #D1D5DB",
                    borderRadius: "8px 0 0 8px",
                    color: "#6B7280",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#E5E7EB",
                    },
                  }}
                  onClick={() => handleArrowClick("backward")}
                  size="small"
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
                </Button>
              </div>
            )}
            <div
              ref={toolRef}
              className="flex-grow flex items-center justify-between h-full max-w-full gap-1 overflow-hidden"
            >
              <div className="flex gap-5 items-center">
                {leftArray?.map((item, index) => {
                  if (item.tooltip) {
                    return (
                      <ToolTipComponent key={`left-${item.id}-${index}`} arrow content={item.tooltip}>
                        {renderToolbarItem(item, index)}
                      </ToolTipComponent>
                    );
                  }
                  return renderToolbarItem(item, index);
                })}
              </div>
              <div className="flex items-center">
                {rightArray?.map((item, index) => {
                  if (item.tooltip) {
                    return (
                      <ToolTipComponent key={`right-scrollable-${item.id}-${index}`} arrow content={item.tooltip}>
                        {renderToolbarItem(item, index)}
                      </ToolTipComponent>
                    );
                  }
                  return renderToolbarItem(item, index);
                })}
              </div>
            </div>

            <div className="flex-shrink-0 flex items-center">
              {leftStickyrray?.map((item, index) => {
                if (item.tooltip) {
                  return (
                    <ToolTipComponent key={`right-scrollable-${item.id}-${index}`} arrow content={item.tooltip}>
                      {renderToolbarItem(item, index)}
                    </ToolTipComponent>
                  );
                }
                return renderToolbarItem(item, index);
              })}
              {enableColumnChooser && (
                <>
                  <ButtonComponent
                    buttonType={BUTTON_TYPES.TEXT_BUTTON}
                    className="ms-2"
                    onClick={handleColumnChooserOpen}
                  >
                    <div className="flex items-center gap-1">
                      {t("Column Chooser")} <ArrowDropDownIcon />
                    </div>
                  </ButtonComponent>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleColumnChooserClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    slotProps={{
                      paper: {
                        sx: {
                          overflow: "auto",
                          maxHeight: 500,
                        },
                      },
                    }}
                  >
                    <div
                      style={{
                        padding: "8px 16px",
                        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#fff",
                        zIndex: 1,
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder={t("Search columns...")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        InputProps={{ disableUnderline: true }}
                        autoFocus
                        sx={{
                          paddingBottom: "4px",
                        }}
                      />
                    </div>
                    <div style={{ overflowY: "auto", flexGrow: 1 }}>
                      {(() => {
                        const filteredColumns = columnDefinitions
                          .filter((col) => col.id !== "_checkbox_selector")
                          .filter((col) => {
                            const nameString = typeof col.name === "string" ? col.name : col.name?.textContent || "";
                            return nameString.toLowerCase().includes(searchTerm.toLowerCase());
                          });

                        if (filteredColumns.length === 0) {
                          return (
                            <Typography
                              variant="body2"
                              sx={{
                                p: 2,
                                color: "text.secondary",
                                textAlign: "center",
                              }}
                            >
                              No matching columns
                            </Typography>
                          );
                        }

                        return filteredColumns.map((col) => (
                          <MenuItem
                            key={col.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleColumnsChange(col);
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              py: 0.5,
                              px: 2,
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                              },
                            }}
                            disableRipple
                          >
                            <CheckBoxComponent
                              checked={selectedColumns.some((c) => c.id === col.id)}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleColumnsChange(col);
                              }}
                            />
                            <span id={`col-label-${col.id}`}>
                              {typeof col.name === "string" ? col.name : col.name?.textContent || ""}
                            </span>
                          </MenuItem>
                        ));
                      })()}
                    </div>
                  </Menu>
                </>
              )}
              {excelExport && (
                <ButtonComponent className="me-2" buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={handleExportToExcel}>
                  <div className="flex gap-1 items-center">
                    <IconComponent className="w-4 h-4" name="ExcelExportIcon" />
                    {t("Export")}
                  </div>
                </ButtonComponent>
              )}
              {propertiesPanel && (
                <IconButton onClick={handleToggleProperties} className={enableColumnChooser ? "ms-2" : ""}>
                  <IconComponent name={showPropertiesPanel ? "PropertiesPanelOpen" : "PropertiesPanelClosed"} />
                </IconButton>
              )}
              {rightStickyArray?.map((item, index) => {
                if (item.tooltip) {
                  return (
                    <ToolTipComponent key={`right-scrollable-${item.id}-${index}`} arrow content={item.tooltip}>
                      {renderToolbarItem(item, index)}
                    </ToolTipComponent>
                  );
                }
                return renderToolbarItem(item, index);
              })}
            </div>

            {showOverFlowBtns && (
              <div className="flex-shrink-0 flex items-center h-full cursor-pointer">
                <Button
                  sx={{
                    height: "100%",
                    minWidth: "36px",
                    borderInlineStart: "1px solid #D1D5DB",
                    borderRadius: "0 8px 8px 0",
                    color: "#6B7280",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#E5E7EB",
                    },
                  }}
                  onClick={() => handleArrowClick("forward")}
                  size="small"
                >
                  <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
                </Button>
              </div>
            )}
          </div>
        )}
      </>
    );
  },
);
ToolBar.displayName = "ToolBar";

ToolBar.propTypes = {
  excelExport: PropTypes.bool,
  excelExportExtension: PropTypes.shape({
    exportToExcel: PropTypes.func.isRequired,
  }),
};

ToolBar.defaultProps = {
  excelExport: false,
  excelExportExtension: undefined,
};
export default ToolBar;
